/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */

import type { IFluidDependencySynthesizer } from "./IFluidDependencySynthesizer.js";

/**
 * This is a condensed version of Record that requires the object has all
 * the FluidObject properties as its type mapped to a string representation
 * of that property.
 *
 * @example
 *
 * ```typescript
 * { IFoo: "IFoo" }
 * ```
 * @legacy @beta
 */
export type FluidObjectSymbolProvider<T> = {
	[P in keyof T]?: P;
};

/**
 * This is a condensed version of Record that requires the object has all
 * the FluidObject properties as its type mapped to an object that implements
 * the property.
 * @legacy @beta
 */
export type AsyncRequiredFluidObjectProvider<T> = T extends undefined
	? Record<string, never>
	: {
			// eslint-disable-next-line @rushstack/no-new-null --  supported by JavaScript; it isn't deprecated
			[P in keyof T]: Promise<NonNullable<Exclude<T[P], undefined | null>>>;
		};

/**
 * This is a condensed version of Record that requires the object has all
 * the FluidObject properties as its type, mapped to an object that implements
 * the property or undefined.
 * @legacy @beta
 */
export type AsyncOptionalFluidObjectProvider<T> = T extends undefined
	? Record<string, never>
	: {
			[P in keyof T]?: Promise<T[P] | undefined>;
		};

/**
 * Combined type for Optional and Required Async Fluid object Providers
 * @legacy @beta
 */
export type AsyncFluidObjectProvider<O, R = undefined> = AsyncOptionalFluidObjectProvider<O> &
	AsyncRequiredFluidObjectProvider<R>;

/**
 * Multiple ways to provide a Fluid object.
 * @legacy @beta
 */
export type FluidObjectProvider<T> =
	| NonNullable<T>
	| Promise<NonNullable<T>>
	| ((dependencyContainer: IFluidDependencySynthesizer) => NonNullable<T>)
	| ((dependencyContainer: IFluidDependencySynthesizer) => Promise<NonNullable<T>>);
